import { CommonModule } from '@angular/common';
import { Component, ElementRef, AfterViewInit, ViewChild, Input, OnInit, OnDestroy } from '@angular/core';
import { CardModule } from 'primeng/card';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SalesPersonRankingComponent } from '../components/dashboard/sales-person-ranking/sales-person-ranking.component';
import { SalesReportOverviewComponent } from '../components/dashboard/sales-report-overview/sales-report-overview.component';
import { MovingItemsSectionComponent } from '../components/dashboard/moving-items-section/moving-items-section.component';
import { DashboardService } from '../pages/service/dashboard.service';
import { Chart, ChartConfiguration } from 'chart.js';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { Router } from '@angular/router';
import { ToolbarModule } from 'primeng/toolbar';
import { ButtonModule } from 'primeng/button';
import { UserRole } from '../auth/roles.enum';
import { AppFeature } from '../auth/features.enum';
import { RoleAccessService } from '../auth/role-access.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { formatDate } from '@angular/common';
import { LoaderService } from '../services/loader.service';

@Component({
  selector: 'app-user-dashboard',
  imports: [ButtonModule, FormsModule, ToolbarModule, CalendarModule, TableModule, ProgressBarModule, CommonModule, CardModule, TagModule, ProgressSpinnerModule, MovingItemsSectionComponent],
  templateUrl: './user-dashboard.component.html',
  styleUrl: './user-dashboard.component.scss'
})
export class UserDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  progress: any;

  @Input() salesData: any[] = [];
  @Input() fastMovingItems: any[] = [];
  @Input() slowMovingItems: any[] = [];

  @ViewChild('barChart', { static: false }) barChart!: ElementRef;
  chart: Chart | null = null;

  fromDate: string = '';
  toDate: string = '';
  todayDate: string = '';

  totalOrderLineAmount = 0;
  totalSalesPersons = 0;
  averageOrderLineAmount = 0;
  maxOrderLineAmount = 0;
  errorMsg!: string;

  ordersPunched = 0;
  achievement = 0;
  target = 0;
  message: string = '';

  constructor(private dashboardService: DashboardService, private router: Router, private roleAccessService: RoleAccessService, private loaderService: LoaderService) {}
  canAccess = false;

  ngOnInit() {
    console.log('User Dashboard Component Initialized');
    // Set todayDate for max attribute
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.todayDate = formatDate(today, 'yyyy-MM-dd', 'en');

    // Set fromDate to 1 week ago, toDate to today (as strings)
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    this.fromDate = formatDate(weekAgo, 'yyyy-MM-dd', 'en');
    this.toDate = formatDate(today, 'yyyy-MM-dd', 'en');

    // Role access check
      const userRoles: UserRole[] = JSON.parse(localStorage.getItem('roleGroup') || '[]');
  this.canAccess = this.roleAccessService.hasAccess(userRoles, AppFeature.UserDashboard);
  if (this.canAccess) {
    this.router.navigate(['/Orders1']);
    return;
  }console.log('User roles:', userRoles);
 
    console.log('User roles:', userRoles);
  this.loaderService.show('Loading User Data','Please wait...');

    // Call getUserDashboard and map the fields
    this.dashboardService.getUserDashboard().pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.loaderService.hide();
        this.ordersPunched = data.ordersPunched ?? 0;
        this.achievement = data.achievement ?? 0;
        this.target = data.target ?? 0;
        this.progress = this.target ? +((this.achievement / this.target) * 100).toFixed(2) : 0;
        this.message = data.message;
      },
      error: (err) => {
        this.loaderService.hide();
        console.error('Error fetching user dashboard:', err);
        this.ordersPunched = 0;
        this.achievement = 0;
        this.target = 0;
      }
    });

    this.loadMovingItems();
  }

  onDateChange(event?: KeyboardEvent) {
    // Validate date format (yyyy-MM-dd)
    if (!this.isValidDateString(this.fromDate)) {
      this.fromDate = '';
    }
    if (!this.isValidDateString(this.toDate)) {
      this.toDate = '';
    }

    // If both dates are selected and toDate is before fromDate, reset toDate
    if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
      this.toDate = '';
    }

    // Only trigger API call if Enter key is pressed or date selected via picker
    if (!event || (event instanceof KeyboardEvent && event.key === 'Enter')) {
      if (this.fromDate && this.toDate) {
        this.loadMovingItems();
      }
    }
  }

  // Helper to validate yyyy-MM-dd string
  private isValidDateString(dateStr: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  }

  // Helper to format Date to yyyy-MM-dd
  private formatDateLocal(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  async loadMovingItems() {
    // Use local date formatting instead of toISOString
    let fromDateStr = this.fromDate;
    let toDateStr = this.toDate;

    // If either is missing, default both to the current defaults (1 week ago and today)
    if (!fromDateStr || !toDateStr) {
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      fromDateStr = this.formatDateLocal(weekAgo);
      toDateStr = this.formatDateLocal(today);
      this.fromDate = fromDateStr;
      this.toDate = toDateStr;
    }

    try {
      const [fast, slow] = await Promise.all([
        firstValueFrom(this.dashboardService.getTopFiveItemsSold(fromDateStr, toDateStr)),
        firstValueFrom(this.dashboardService.getLeastFiveItemsSold(fromDateStr, toDateStr))
      ]);
      this.fastMovingItems = fast;
      this.slowMovingItems = slow;
    } catch (err) {
      console.error('Error loading moving items:', err);
      this.fastMovingItems = [];
      this.slowMovingItems = [];
    }
  }

  ngOnChanges() {
    this.calculateMetrics();
  }

  ngAfterViewInit() {
    this.renderBarChart();
  }

  calculateMetrics() {
    if (this.salesData.length > 0) {
      this.totalOrderLineAmount = this.salesData.reduce((sum, item) => sum + (item.orderLineAmount || 0), 0);
      this.totalSalesPersons = this.salesData.length;
      this.averageOrderLineAmount = this.totalOrderLineAmount / this.totalSalesPersons;
      this.maxOrderLineAmount = Math.max(...this.salesData.map(item => item.orderLineAmount || 0));
    } else {
      this.totalOrderLineAmount = 0;
      this.totalSalesPersons = 0;
      this.averageOrderLineAmount = 0;
      this.maxOrderLineAmount = 0;
    }
  }

  renderBarChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    if (this.barChart && this.salesData && this.salesData.length > 0) {
      const ctx = this.barChart.nativeElement.getContext('2d');
      const config: ChartConfiguration = {
        type: 'bar',
        data: {
          labels: this.salesData.map(sale => `${sale.firstName} ${sale.lastName}`),
          datasets: [{
            label: 'Order Line Amount',
            data: this.salesData.map(sale => sale.orderLineAmount),
            backgroundColor: '#667eea'
          }]
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false }
          },
          scales: {
            x: { title: { display: true, text: 'Sales Person' } },
            y: { title: { display: true, text: 'Order Line Amount' }, beginAtZero: true }
          }
        }
      };
      this.chart = new Chart(ctx, config);
    }
  }

  goBack() {
    this.router.navigate(['/display']);
  }

  goToOrderHeader() {
    this.router.navigate(['/Orders1']);
  }

  ngOnDestroy(): void {
    // Signal all observables to complete and clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    console.log('UserDashboardComponent destroyed');
  }
}